import { Button, Card, CardContent, Dialog, DialogContent, Grid, Icon, IconButton, Stack, Tooltip } from '@mui/material'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import StepperDialog from 'components/modal/StepperDialog'
import { intersectionWith, isNil, flatten, groupBy, keys, includes, uniqBy, compact, pickBy } from 'lodash'
import { normalizeString } from 'utils/StringUtil'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import useListIndexed from 'utils/customHook/useListIndexed'
import { getParametersNature, getParametersType } from 'referencial/util/ReferencialUtils'
import { getStatusInformation } from 'referencial/util/StatusUtils'
import { hybridSearch } from 'utils/SearchUtil'
import SimpleSelectionTable, { ActiveParameterFilterField } from 'components/datatable/SimpleSelectionTable'
import useSandreList from 'utils/customHook/useSandreList'
import useBoolean from 'utils/customHook/useBoolean'
import useUpdateEffect from 'utils/customHook/useUpdateEffect'
import { getLocalizationPicto } from 'utils/AnalyseUtils'
import { ALIGN, CardTable } from 'components/datatable/NewTable'
import { nbPerPageLabel } from 'referencial/constants/ReferencialConstants'
import Select from 'components/forms/Select'
import Checkbox from 'components/forms/Checkbox'
import useDebounce from 'utils/customHook/useDebounce'
import Input from 'components/forms/Input'
import NoteAddIcon from '@mui/icons-material/NoteAdd'
import { darkBlue, mainBlue, skyBlue, whiteColor } from 'utils/constants/ColorTheme'
import { StepperSelection } from 'administration/components/qualitometer/selection/ComponentsOptionSelection'
import ParameterAction from 'referencial/components/parameter/actions/ParameterAction'
import { QualityCampaignContext } from 'quality/components/campaign/QualityCampaign'
import { getDefaultCampaignParameterUnit, negIdGenerator } from 'campaign/utils/CampaignUtils'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { hasValue } from 'utils/NumberUtil'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'

const DIALOG = {
    CHOICE: 'CHOICE',
    PARAMETER: 'PARAMETER',
    SELECTION: 'SELECTION',
}

const ChoiceDialog = ({
    isOpen = false,
    onClose = () => {},
    onChoose = () => {},
}) => {
    return (
        <Dialog
            fullWidth
            maxWidth='md'
            open={isOpen}
        >
            <DefaultDialogTitle title={i18n.chooseImportType} onClose={onClose}/>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid container size={5} sx={{ padding: '2rem' }}>
                        <Grid container size={12} justifyContent='center'>
                            <Icon sx={{ fontSize: '100px' }}>bubble_chart</Icon>
                        </Grid>
                        <Grid container size={12} justifyContent='center'>
                            <Button
                                variant='contained'
                                onClick={() => onChoose(DIALOG.PARAMETER)}
                            >
                                {i18n.parameter}
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid size={2}>
                        <div
                            style={{
                                borderLeft: `1px solid ${mainBlue}`,
                                borderRight: `1px solid ${darkBlue}`,
                                height: '15rem',
                                right: '50%',
                                position: 'absolute',
                            }}
                        />
                    </Grid>
                    <Grid container size={5} sx={{ padding: '2rem' }}>
                        <Grid container size={12} justifyContent='center'>
                            <Icon sx={{ fontSize: '100px' }}>group_work</Icon>
                        </Grid>
                        <Grid container size={12} justifyContent='center'>
                            <Button
                                variant='contained'
                                onClick={() => onChoose(DIALOG.SELECTION)}
                            >
                                {i18n.selection}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    )
}

ChoiceDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onChoose: PropTypes.func,
}

const PARAM_STEP = {
    SELECTION: 0,
    UPDATE: 1,
}

const parameterFilterFunction = (list, { selection = '-1', listParam, searchValue, shouldDisplayNonActive = false }) => {
    const filterActive = shouldDisplayNonActive ? list : list.filter(p => p.active)
    const filterSelection = selection !== '-1' ? intersectionWith(filterActive, listParam, (elem, id) => elem.id === id) : filterActive
    return hybridSearch(filterSelection, normalizeString(searchValue))
}

const StepSelectParameters = ({
    selectedParameters = [],
    setSelectedParameters = () => {},
}) => {
    const {
        parameters,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
    }), shallowEqual)

    const indexedParameters = useListIndexed(parameters, 'code')

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const formattedParameters = useMemo(() => {
        const types = getParametersType()
        const natures = getParametersNature()
        return parameters.map(param => ({
            id: param.code,
            name: `${showSandreCode ? `[${param.code}]` : ''} ${param.name.length > 40 && param.shortLabel || param.name || ''}`,
            type: types[param.type],
            nature: natures[param.nature],
            status: param.status ? getStatusInformation(param.status) : i18n.validated,
            active: param.active === '1',
            _normalizedSearchString: [param.name, param.mnemonique, param.code].filter(l => !!l).map(s => normalizeString(s)),
        }))
    }, [parameters, showSandreCode])

    const onChangeSelected = (list = []) => {
        setSelectedParameters(prevSP => {
            const filteredPrevSP = prevSP.filter(({ parameterCode }) => includes(list, parameterCode))
            return [
                ...filteredPrevSP,
                ...list
                    .filter(code => !includes(filteredPrevSP.map(({ parameterCode }) => parameterCode), code))
                    .map(code => ({
                        id: negIdGenerator.next().value,
                        parameterCode: code,
                        unitCode: getDefaultCampaignParameterUnit(indexedParameters[code]),
                        fractionCode: '0',
                        placeCode: '0',
                        supportCode: '0',
                        selectionCode: undefined,
                    })),
            ]
        })
    }

    return (
        <SimpleSelectionTable
            onChange={onChangeSelected}

            listData={formattedParameters}
            selectedList={uniqBy(selectedParameters, 'parameterCode').map(sp => sp.parameterCode).filter(p => !!indexedParameters[p])}

            listHeaders={['name', 'type', 'nature', 'status']}
            listTitle={i18n.nonSelectedParameters}
            selectedListTitle={i18n.selectedParameters}
            maxHeightTable='43vh'

            filterField={ActiveParameterFilterField}
            filterFunction={parameterFilterFunction}
        />
    )
}

StepSelectParameters.propTypes = {
    selectedParameters: PropTypes.arrayOf(PropTypes.shape({
        parameterCode: PropTypes.string,
    })),
    setSelectedParameters: PropTypes.func,
}

const CardUpdateField = ({
    onValidate = () => {},
}) => {
    const {
        units,
        fractions,
        supports,
    } = useSelector(store => ({
        units: store.UnitReducer.units,
        fractions: store.FractionReducer.fractions,
        supports: store.SupportReducer.supports,
    }), shallowEqual)
    const analysisPlaces = useSandreList('ANALYSES.LIEU')

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const [unit, setUnit] = useState()
    const [fraction, setFraction] = useState()
    const [support, setSupport] = useState()
    const [place, setPlace] = useState() // hasValue(v) && `${v}` || ''
    const { value: shouldDisplayNonActive, toggle: toggleDisplayNonActive } = useBoolean(false)

    const activeSupports = useMemo(() => shouldDisplayNonActive ? supports : supports.filter(s => s.active === '1'), [shouldDisplayNonActive, supports])
    const activeFractions = useMemo(() => shouldDisplayNonActive ? fractions : fractions.filter(s => s.active === '1'), [fractions, shouldDisplayNonActive])
    const activeUnits = useMemo(() => shouldDisplayNonActive ? units : units.filter(s => s.active === '1'), [units, shouldDisplayNonActive])

    return (
        <Card>
            <CardContent>
                <Grid container spacing={1} alignItems='center'>
                    <Grid size={3}>
                        <Select
                            label={i18n.units}
                            value={unit}
                            options={activeUnits}
                            onChange={setUnit}
                            keyValue='id'
                            keyLabel={showSandreCode ? 'labelWithCode' : 'name'}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            label={i18n.fractions}
                            value={fraction}
                            options={activeFractions}
                            onChange={setFraction}
                            keyValue='id'
                            keyLabel={showSandreCode ? 'labelWithCode' : 'name'}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            label={i18n.support}
                            value={support}
                            options={activeSupports}
                            onChange={setSupport}
                            keyValue='id'
                            keyLabel={showSandreCode ? 'labelWithCode' : 'name'}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            options={analysisPlaces}
                            value={place}
                            label={i18n.place}
                            returnNull
                            onChange={setPlace}
                        />
                    </Grid>
                    <Grid size='auto'>
                        <Checkbox
                            label={i18n.displayNonActiveReferentials}
                            checked={shouldDisplayNonActive}
                            onChange={toggleDisplayNonActive}
                        />
                    </Grid>
                    <Grid size='grow' />
                    <Grid size={3}>
                        <Button
                            onClick={() => {
                                onValidate(pickBy({
                                    fractionCode: fraction,
                                    supportCode: support,
                                    placeCode: isNil(place) ? undefined : `${place}`,
                                    unitCode: unit,
                                }, hasValue))
                            }}
                            variant='contained'
                            fullWidth
                        >
                            {i18n.apply}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

CardUpdateField.propTypes = {
    onValidate: PropTypes.func,
}

const StepUpdateParameters = ({
    selectedParameters = [],
    setSelectedParameters = () => {},
}) => {
    const {
        parameters,
        fractions,
        supports,
        units,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        fractions: store.FractionReducer.fractions,
        supports: store.SupportReducer.supports,
        units: store.UnitReducer.units,
    }), shallowEqual)
    const indexedParameters = useListIndexed(parameters, 'code')
    const indexedUnits = useListIndexed(units, 'code')
    const indexedSupports = useListIndexed(supports, 'code')
    const indexedFractions = useListIndexed(fractions, 'code')

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const [tmpSelected, setTmpSelected] = useState([])

    useUpdateEffect(() => {
        setTmpSelected([])
    }, [selectedParameters])

    const formattedParameters = useMemo(() => selectedParameters.map(({ parameterCode, fractionCode, supportCode, placeCode, unitCode, selectionCode }, i) => {
        const isSelected = tmpSelected.some(index => index === i)
        const parameter = indexedParameters[parameterCode]
        return {
            name: parameter?.name.length > 60 ? parameter?.shortLabel : parameter?.name,
            unit: showSandreCode ? indexedUnits[unitCode]?.symbolWithCode : indexedUnits[unitCode]?.symbol,
            fraction: showSandreCode ? indexedFractions[fractionCode]?.labelWithCode : indexedFractions[fractionCode]?.name,
            support: showSandreCode ? indexedSupports[supportCode]?.labelWithCode : indexedSupports[supportCode]?.name,
            place: getLocalizationPicto(placeCode),
            color: isSelected ? skyBlue : whiteColor,
            code: parameterCode,
            selectionCode,
            isSelected,
            index: i,
        }
    }), [selectedParameters, tmpSelected, showSandreCode])
    const actions = tmpSelected.length === selectedParameters.length ? [{
        onClick: () => setTmpSelected([]),
        icon: 'check_box',
        tooltip: i18n.unselectAll,
    }] : [{
        onClick: () => setTmpSelected(selectedParameters.map((_, i) => i)),
        icon: 'check_box_outline_blank',
        tooltip: i18n.selectAll,
    }]

    return (
        <Stack spacing={1}>
            <CardTable
                title={i18n.parameters}
                actions={actions}
                headers={compact([ 'name', 'unit', 'fraction', 'support', 'place', formattedParameters[0]?.selectionCode && 'selectionCode' ])}
                rows={formattedParameters}
                onClickRow={({ index, isSelected }) => {
                    setTmpSelected(prev => {
                        if (isSelected) return prev.filter(i => index !== i)
                        return [...prev, index]
                    })
                }}
                lineActions={compact([
                    ...[{
                        icon: ({ isSelected }) => isSelected ? 'check_box' : 'check_box_outline_blank',
                        align: ALIGN.RIGHT,
                        onClick: ({ index, isSelected }) => {
                            setTmpSelected(prev => {
                                if (isSelected) return prev.filter(i => index !== i)
                                return [...prev, index]
                            })
                        },
                    }],
                    ...flatten([!formattedParameters[0]?.selectionCode ? [{
                        icon: 'close',
                        onClick: ({ index }) => setSelectedParameters(prevSP => prevSP.toSpliced(index, 1)),
                    }, {
                        icon: 'add_to_photos', // duplicate
                        onClick: ({ index }) => setSelectedParameters(prevSP => prevSP.toSpliced(index, 0, { ...prevSP[index] })),
                    }] : undefined]),
                ])}
                maxHeight='45vh'
                rowsPerPageOptions={nbPerPageLabel}
            />
            <CardUpdateField
                onValidate={newValues => {
                    setSelectedParameters(prev => (
                        prev.map((param, i) => includes(tmpSelected, i) ? { ...param, ...newValues } : param)
                    ))
                }}
            />
        </Stack>
    )
}

StepUpdateParameters.propTypes = {
    selectedParameters: PropTypes.arrayOf(PropTypes.shape({
        parameterCode: PropTypes.string,
        fractionCode: PropTypes.string,
        supportCode: PropTypes.string,
        unitCode: PropTypes.string,
        placeCode: PropTypes.string,
    })),
    setSelectedParameters: PropTypes.func,
}

const StepperParameters = ({
    isOpen = false,
    onClose = () => {},
    onValidate = () => {},
    campaignParameters = [],
}) => {
    const [selectedParameters, setSelectedParameters] = useState(campaignParameters.filter(({ selectionCode }) => !selectionCode))

    useEffect(() => {
        setSelectedParameters(campaignParameters.filter(({ selectionCode }) => !selectionCode))
    }, [campaignParameters])

    return (
        <StepperDialog
            steps={[
                {
                    label: i18n.parametersSelection,
                    constant: PARAM_STEP.SELECTION,
                    nextAvailable: selectedParameters.length > 0,
                },
                {
                    label: i18n.parametersModification,
                    constant: PARAM_STEP.UPDATE,
                },
            ]}
            open={isOpen}
            title={i18n.parameters}
            closeDialog={() => {
                onClose()
                setSelectedParameters(campaignParameters.filter(({ selectionCode }) => !selectionCode))
            }}
            renderSaveButton={step => (step === PARAM_STEP.UPDATE) && (
                <Button
                    onClick={() => onValidate([
                        ...campaignParameters.filter(({ selectionCode }) => hasValue(selectionCode)),
                        ...selectedParameters,
                    ])}
                    variant='contained'
                >
                    {i18n.validate}
                </Button>
            )}
            sx={{ minWidth: '80vw' }}
        >
            {step => (
                <>
                    {step === PARAM_STEP.SELECTION && (
                        <StepSelectParameters
                            selectedParameters={selectedParameters}
                            setSelectedParameters={setSelectedParameters}
                        />
                    )}
                    {step === PARAM_STEP.UPDATE && (
                        <StepUpdateParameters
                            selectedParameters={selectedParameters}
                            setSelectedParameters={setSelectedParameters}
                        />
                    )}
                </>
            )}
        </StepperDialog>
    )
}

StepperParameters.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    campaignParameters: PropTypes.arrayOf(PropTypes.shape({
        parameterCode: PropTypes.string,
        fractionCode: PropTypes.string,
        supportCode: PropTypes.string,
        unitCode: PropTypes.string,
        placeCode: PropTypes.string,
    })),
}

const SELECTION_STEP = {
    SELECTION: 0,
    UPDATE: 1,
}
const DEFAULT_SELECTION = { listType: 0, users: [], administrators: [] }
const DEFAULT_QUALITY_SELECTION = { selections: [] }

const selectionFilterFunction = (list, { searchValue }) => {
    return hybridSearch(list.filter(e => !isNil(e)), normalizeString(searchValue))
}

const SelectionFilterField = ({
    filter = {},
    setFilter = () => { },
}) => {
    const dispatch = useDispatch()
    const [stateFilter, setStateFilter] = useState(filter)

    const {
        value: isCreateSelectionOpen,
        setFalse: onCloseSelectionCreate,
        setTrue: onOpenSelectionCreate,
    } = useBoolean(false)

    useDebounce(() => {
        setFilter(stateFilter)
    }, 500, [stateFilter])

    return (
        <Grid container spacing={1} alignItems='center'>
            <Grid size='grow'>
                <Input
                    title={i18n.search}
                    value={stateFilter.searchValue}
                    onChange={searchValue => setStateFilter(prev => ({ ...prev, searchValue }))}
                />
            </Grid>
            <Grid size='auto'>
                <Tooltip title={i18n.createNewSelection}>
                    <IconButton sx={{ fontSize: '24px', color: darkBlue }} onClick={onOpenSelectionCreate}>
                        <NoteAddIcon fontSize='inherit' />
                    </IconButton>
                </Tooltip>
                <StepperSelection
                    isOpen={isCreateSelectionOpen}
                    onClose={onCloseSelectionCreate}
                    onSave={(selection, qualitySelection) => {
                        const newSelection = { ...selection, parameters: qualitySelection.selections.map(qs => qs.parameterCode) }
                        dispatch(ParameterAction.createSelection(newSelection)).then(() => {
                            dispatch(ParameterAction.fetchSelectionsWithParam()).finally(onCloseSelectionCreate)
                        })
                    }}
                    selectedSelection={DEFAULT_SELECTION}
                    selectedQualitySelection={DEFAULT_QUALITY_SELECTION}
                />
            </Grid>
        </Grid>
    )
}

SelectionFilterField.propTypes = {
    filter: PropTypes.shape({
        searchValue: PropTypes.string,
    }),
    setFilter: PropTypes.func,
    onCreate: PropTypes.func,
}

const StepSelectSelections = ({
    selectedSelections = [],
    setSelectedSelections = () => {},
}) => {
    const {
        selections,
    } = useSelector(store => ({
        selections: store.ParameterReducer.selectionsWithParam,
    }), shallowEqual)

    const formattedSelections = selections.map(selection => {
        const {
            code,
            name = '',
            parameters = [],
        } = selection
        return {
            id: code,
            name,
            nbParam: `${parameters.length}`,
            _normalizedSearchString: [code, name].map(str => normalizeString(str)).filter(l => !!l),
        }
    })

    return (
        <SimpleSelectionTable
            onChange={setSelectedSelections}

            listData={formattedSelections}
            selectedList={selectedSelections}

            listHeaders={['name', 'nbParam']}
            listTitle={i18n.nonSelectedSelections}
            selectedListTitle={i18n.selectedSelections}
            maxHeightTable='52vh'

            filterField={SelectionFilterField}
            filterFunction={selectionFilterFunction}
        />
    )
}

StepSelectSelections.propTypes = {
    selectedSelections: PropTypes.arrayOf(PropTypes.string),
    setSelectedSelections: PropTypes.func,
}

const StepperSelections = ({
    isOpen = false,
    onClose = () => {},
    onValidate = () => {},
    campaignParameters = [],
    campaignSelections = [],
}) => {
    const {
        parameters,
        selections,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        selections: store.ParameterReducer.selectionsWithParam,
    }), shallowEqual)
    const indexedParameters = useListIndexed(parameters, 'code')
    const indexedSelections = useListIndexed(selections, 'code')

    const [selectedParameters, setSelectedParameters] = useState(campaignParameters.filter(({ selectionCode }) => hasValue(selectionCode)))
    const [selectedSelections, setSelectedSelections] = useState(campaignSelections)

    useEffect(() => {
        setSelectedParameters(campaignParameters.filter(({ selectionCode }) => hasValue(selectionCode)))
    }, [campaignParameters])

    useEffect(() => {
        setSelectedSelections(campaignSelections)
    }, [campaignSelections])

    return (
        <StepperDialog
            defaultStep={SELECTION_STEP.SELECTION}
            steps={[
                {
                    label: i18n.parametersSelectionsSelection,
                    constant: SELECTION_STEP.SELECTION,
                    nextAvailable: selectedSelections.length > 0,
                    onNext: (nextStep) => {
                        setSelectedParameters(prevSP => {
                            const filteredPrev = prevSP.filter(({ selectionCode }) => includes(selectedSelections, selectionCode))
                            const oldSelectionCodes = prevSP.map(({ selectionCode }) => selectionCode)
                            const newParametersCodesBySelection = groupBy(flatten(selectedSelections
                                .filter(newSelCode => !includes(oldSelectionCodes, newSelCode))
                                .map(code => (indexedSelections[code]?.parameters ?? [])
                                    .map(param => ({ param, selectionCode: code })),
                                )), 'selectionCode')
                            return [
                                ...filteredPrev,
                                ...flatten(keys(newParametersCodesBySelection).map(selectionCode => (
                                    newParametersCodesBySelection[selectionCode].map(({ param }) => {
                                        return {
                                            id: negIdGenerator.next().value,
                                            parameterCode: param,
                                            unitCode: getDefaultCampaignParameterUnit(indexedParameters[param]),
                                            fractionCode: '0',
                                            placeCode: '0',
                                            supportCode: '0',
                                            selectionCode,
                                        }
                                    })
                                )) ?? []),
                            ]
                        })
                        nextStep()
                    },
                },
                {
                    label: i18n.parametersModification,
                    constant: SELECTION_STEP.UPDATE,
                },
            ]}
            open={isOpen}
            title={i18n.selections}
            closeDialog={() => {
                onClose()
                setSelectedParameters(campaignParameters)
                setSelectedSelections(campaignSelections)
            }}
            renderSaveButton={step => (step === SELECTION_STEP.UPDATE) && (
                <Button
                    onClick={() => onValidate([
                        ...selectedParameters,
                        ...campaignParameters.filter(({ selectionCode }) => !selectionCode),
                    ], selectedSelections)}
                    variant='contained'
                >
                    {i18n.validate}
                </Button>
            )}
            sx={{ minWidth: '80vw', minHeight: '95vh' }}
        >
            {
                step => (
                    <>
                        {step === SELECTION_STEP.SELECTION && (
                            <StepSelectSelections
                                selectedSelections={selectedSelections}
                                setSelectedSelections={setSelectedSelections}
                            />
                        )}
                        {step === SELECTION_STEP.UPDATE && (
                            <StepUpdateParameters
                                selectedParameters={selectedParameters}
                                setSelectedParameters={setSelectedParameters}
                            />
                        )}
                    </>
                )
            }
        </StepperDialog>
    )
}

StepperSelections.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    campaignParameters: PropTypes.arrayOf(PropTypes.shape({})),
    campaignSelections: PropTypes.arrayOf(PropTypes.shape({})),
}

const PopinQualityCampaignParameters = ({
    isOpen = false,
    onClose = () => {},
    onValidate = () => {},
}) => {
    const { campaign: { selections: campaignSelections }, campaignParameters } = useContext(QualityCampaignContext)
    const [displayDialog, setDisplayDialog] = useState(DIALOG.CHOICE)

    useEffect(() => {
        if (isOpen) return
        setDisplayDialog(DIALOG.CHOICE)
    }, [isOpen])

    return (
        <>
            <ChoiceDialog
                isOpen={isOpen && displayDialog === DIALOG.CHOICE}
                onClose={onClose}
                onChoose={setDisplayDialog}
            />
            <StepperParameters
                isOpen={isOpen && displayDialog === DIALOG.PARAMETER}
                onClose={onClose}
                onValidate={onValidate}
                campaignParameters={campaignParameters}
            />
            <StepperSelections
                isOpen={isOpen && displayDialog === DIALOG.SELECTION}
                onClose={onClose}
                onValidate={onValidate}
                campaignParameters={campaignParameters}
                campaignSelections={campaignSelections}
            />
        </>
    )
}

PopinQualityCampaignParameters.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    campaignParameters: PropTypes.arrayOf(PropTypes.shape({})),
    campaignSelections: PropTypes.arrayOf(PropTypes.shape({})),
}

export default PopinQualityCampaignParameters